import Button from '@jetbrains/ring-ui/components/button/button'
import Link from '@jetbrains/ring-ui/components/link/link'
import React, {useState} from 'react'

import {useJobId} from '../../../../../../../EditPipelinePage/EditPipelinePage.hooks'
import {pipelineDraft} from '../../../../../../../EditPipelinePage/slices/EditPipelinePage.slices'
import {usePipelineId} from '../../../../../../../hooks/pipeline'
import {areFeaturesWithoutBackendEnabled} from '../../../../../../../utils/featureToggles'
import Details from '../../../../../Details/Details'
import EditSettingsForm from '../../../../../EditSettingsForm/EditSettingsForm'
import Input, {Size} from '../../../../../Input'
import {IntegrationType} from '../../Integrations.consts'
import {useGenerateFreeIntegrationName} from '../../Integrations.hooks'

import {npmDetailsLink, tokenPlaceholder, npmDefaultRegistryHost} from './NpmIntegrationForm.consts'

import styles from './NpmIntegrationForm.css'

import {useAppDispatch} from 'src/hooks/react-redux'
import {restApi} from 'src/services/rest'

type Props = {
  id?: string
  parameters?: Record<string, string>
  onClose: () => void
}

function NpmIntegrationForm({onClose, id, parameters}: Props) {
  const jobId = useJobId()
  const pipelineId = usePipelineId()

  const [url, setUrl] = useState(parameters?.npmRegistryHost ?? npmDefaultRegistryHost)
  const [scope, setScope] = useState(parameters?.npmRegistryScope ?? '')
  const isEditMode = id != null && parameters != null
  const hasToken = isEditMode && parameters['secure:npmRegistryToken'] !== ''
  const [token, setToken] = useState(hasToken ? tokenPlaceholder : '')

  const generateFreeIntegrationName = useGenerateFreeIntegrationName('NPM')
  const dispatch = useAppDispatch()

  const [testNpmConnection, {testResult, isTesting}] =
    restApi.endpoints.testNpmConnection.useMutation({
      selectFromResult: ({data, isLoading}) => ({
        testResult: data,
        isTesting: isLoading,
      }),
    })
  const errors = [...(testResult?.querySelectorAll('error') ?? [])]

  const confirm = () => {
    const newParameters: Record<string, string> = {
      npmRegistryHost: url,
      npmRegistryScope: scope,
    }
    if (!hasToken || token !== tokenPlaceholder) {
      newParameters['secure:npmRegistryToken'] = token
    }

    dispatch(
      isEditMode
        ? pipelineDraft.actions.editIntegration({
            pipelineId,
            id,
            parameters: newParameters,
          })
        : pipelineDraft.actions.addIntegration({
            pipelineId,
            jobId,
            type: IntegrationType.NPM,
            parameters: {
              ...newParameters,
              displayName: generateFreeIntegrationName(),
            },
          }),
    )

    onClose()
  }

  const deleteHandler = () => {
    dispatch(
      pipelineDraft.actions.deleteIntegration({
        id: id!,
        name: parameters!.displayName!,
        pipelineId,
      }),
    )

    onClose()
  }

  return (
    <EditSettingsForm
      className={styles.npmIntegrationForm}
      onConfirmClick={confirm}
      onCancelClick={onClose}
      onDeleteClick={isEditMode ? deleteHandler : undefined}
    >
      <Input
        label={'URL'}
        type="url"
        spellCheck={false}
        size={Size.FULL}
        value={url}
        onChange={e => setUrl(e.currentTarget.value)}
        autoFocus
      />
      <Input
        size={Size.FULL}
        aria-label={'Scope'}
        label={
          <>
            {'Scope'}
            <Details>
              {
                'Specify a scope for multiple registries. Leave empty to use this registry as default for all npm/yarn commands. '
              }
              <Link target="_blank" rel="noreferrer" href={npmDetailsLink}>
                {'Details'}
              </Link>
            </Details>
          </>
        }
        value={scope}
        onChange={e => setScope(e.currentTarget.value)}
      />
      <Input
        label={'Access Token'}
        size={Size.FULL}
        data-imitate-password
        placeholder={'Leave empty for anonymous'}
        value={token}
        onChange={e => setToken(e.currentTarget.value)}
      />
      {areFeaturesWithoutBackendEnabled && (
        <div className={styles.testNpmConnectionBlock}>
          <Button
            className={styles.testNpmConnection}
            onClick={() => testNpmConnection({url, scope, token})}
            loader={isTesting}
            text
          >
            {'Test Connection'}
          </Button>
          {testResult != null &&
            (errors.length > 0 ? (
              errors.map(error => (
                <span key={error.textContent} className={styles.error}>
                  {error.textContent}
                </span>
              ))
            ) : (
              <span className={styles.success}>{'Success'}</span>
            ))}
        </div>
      )}
    </EditSettingsForm>
  )
}

export default NpmIntegrationForm
